import type { KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftRight, Trash2, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import { useDeleteComparison, ComparisonListItem } from '@/reactQuery/comparison';

interface ComparisonCardProps {
  comparison: ComparisonListItem;
  onView: () => void;
  onRegenerate?: (filterIdA: string, filterIdB: string) => void;
  isRegenerating?: boolean;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  // Honour the browser locale instead of hardcoding en-GB so a Dutch
  // user sees "8 apr. 2026" and an English user sees "8 Apr 2026".
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function ComparisonCard({ comparison, onView, onRegenerate, isRegenerating }: ComparisonCardProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const { mutate: deleteComparison, isPending: isDeleting } = useDeleteComparison();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteComparison(
      { projectId: projectId!, comparisonId: comparison._id },
      {
        onSuccess: () => toast.success(lang.get('msg.comparisonDeleted')),
        onError: () => toast.error(lang.get('msg.comparisonDeleteFailed')),
      },
    );
  };

  // The whole card is a click target for the View action, but it must
  // also be keyboard-reachable and announce as interactive. role="button"
  // + tabIndex=0 + Enter/Space key handler matches the pattern in
  // ThreadList and is the minimum needed for a div-as-button to pass
  // WCAG. The inner <Button> below calls the same onView so mouse users
  // have redundant affordance.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onView();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="flex items-center justify-between gap-4 px-4 py-3.5 rounded-lg border border-neutral-200 bg-white hover:border-neutral-300 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
      onClick={onView}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="flex items-center justify-center h-8 w-8 rounded-md bg-emerald-50 shrink-0 mt-0.5">
          <ArrowLeftRight className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-neutral-900 truncate">
              "{comparison.filter_a_name}" vs "{comparison.filter_b_name}"
            </p>
            {comparison.is_stale && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 rounded">
                <AlertTriangle className="h-3 w-3" />
                {lang.get('msg.stale')}
              </span>
            )}
          </div>
          <p className="text-xs text-neutral-500 mt-0.5">
            {comparison.sample_size_a} {lang.get('msg.respondents')} ·{' '}
            {comparison.sample_size_b} {lang.get('msg.respondents')}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-neutral-400 mr-1">{formatDate(comparison.createdAt)}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          className="text-neutral-600 hover:text-neutral-900"
        >
          {lang.get('msg.view')}
        </Button>
        {comparison.is_stale && onRegenerate && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onRegenerate(comparison.filter_id_a, comparison.filter_id_b);
            }}
            disabled={isRegenerating}
            className="text-amber-600 hover:text-amber-800 gap-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {lang.get('msg.regenerate')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={isDeleting}
          className="text-neutral-400 hover:text-red-600"
        >
          {isDeleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
    </div>
  );
}

export default ComparisonCard;
